import { ActionGroupTypes, ActionStatusTypes } from "../../../enum/ActionStatusTypes";
import * as logger from "../../../logger";
import { getNodeById } from "../../../nodeSelector";
import * as nodeUtils from "../../../utils/nodeUtils";
import * as pathUtils from "../../../utils/pathUtils";
import * as systemUtils from "../../../utils/systemUtils";
import { ACTION_PREFIX_PARAMETER } from "./abstract/parameterAbstract";
import {
  getParameterReferences,
  processParameterReferences,
  trimIdReference,
} from "./helpers/parameterHelper";

const _action = `${ACTION_PREFIX_PARAMETER}_move_flat`;
const _header = `Move parameter`;

export const moveParameterFlat = (root, id, idDestinationParent, indexDestination) => {
  const _payload = moveParameterFlatLogic(root, id, idDestinationParent, indexDestination);

  logger.logAction(_action, _payload, ACTION_PREFIX_PARAMETER);

  return { root, info: _payload };
};

//--------------------------------------------------------------------------------

export const moveParameterFlatLogic = (root, id, idDestinationParent, indexDestination) => {
  const _idParent = pathUtils.getIdParent(id);
  const { data: _node, index: _index } = getNodeById(root, id);
  const { data: _nodeParent } = getNodeById(root, _idParent);
  const { data: _nodeDestinationParent } = getNodeById(root, idDestinationParent);
  if (!_nodeDestinationParent) {
    throw new Error("Destination parent node not found");
  }
  const { data: _nodeCopy, idTrack: _idTrack } = nodeUtils.copy(_node, {
    idParent: idDestinationParent,
  });

  const _indexParsed =
    indexDestination ??
    (_idParent === idDestinationParent
      ? nodeUtils.getParameters(_nodeDestinationParent).length - 1
      : nodeUtils.getParameters(_nodeDestinationParent).length);

  const _nodeBackup = systemUtils.createDeepCopy(_node);
  const _references = getParameterReferences(root, _node);

  if (_nodeParent !== _nodeDestinationParent) {
    const _destParameters = nodeUtils
      .getParameters(_nodeDestinationParent)
      .map((e) => nodeUtils.getName(e));

    if (_destParameters.includes(nodeUtils.getName(_nodeCopy))) {
      let _updateName = nodeUtils.getName(_nodeCopy);
      let _index = 1;

      while (_destParameters.includes(`${_updateName} - ${_index}`)) {
        _index++;
      }

      nodeUtils.setName(_nodeCopy, `${_updateName} - ${_index}`, true);
    }
  }

  detachNode(_nodeParent, id);
  attachNode(_nodeDestinationParent, _nodeCopy, _indexParsed);

  handleReferences(root, _references, _idTrack);

  return generatePayload(
    _nodeBackup,
    _index,
    _nodeParent,
    _nodeDestinationParent,
    _indexParsed,
    _references.description
  );
};

const detachNode = (nodeParent, id) => {
  const fun = (e) => e.filter((f) => nodeUtils.getID(f) !== id);
  nodeUtils.modifyParameters(nodeParent, fun, true);
};

const attachNode = (nodeDestinationParent, nodeCopy, index) => {
  const funAdd = (e) => e.push(nodeCopy);
  const funPosition = (e) => e.splice(index, 0, nodeCopy);
  const fun = (e) => (!index && index !== 0 ? funAdd(e) : funPosition(e));
  nodeUtils.modifyParameters(nodeDestinationParent, fun, false);
};

const handleReferences = (root, references, idTrack) => {
  const funTarget = (e) => {
    nodeUtils.addIdReference(e.dst, idTrack.get(nodeUtils.getID(e.src)));
    trimIdReference(root, e.dst);
  };

  const funReference = (e) => {
    nodeUtils.addIdReference(e.src, idTrack.get(nodeUtils.getID(e.dst)));
    trimIdReference(root, e.src);
  };

  processParameterReferences(references, funTarget, funTarget, funReference, funReference);
};

//--------------------------------------------------------------------------------

const generatePayload = (
  node,
  index,
  nodeParent,
  nodeDestinationParent,
  indexDestination,
  uuidReferences
) => {
  const _payload = {
    group: ActionGroupTypes.UPDATE,
    status: ActionStatusTypes.SUCCESS,
    action: _action,
    time: systemUtils.getTimestamp(),
    header: _header,
    description: generatePayloadDescription(node, nodeParent, nodeDestinationParent),
    node: node,
    uuid: nodeUtils.getUUID(node),
    uuidParent: nodeUtils.getUUID(nodeParent),
    index: index,
    uuidDestinationParent: nodeUtils.getUUID(nodeDestinationParent),
    indexDestination: indexDestination,
  };

  return { ..._payload, ...uuidReferences };
};

const generatePayloadDescription = (node, nodeParent, nodeDestinationParent) => {
  return `Move parameter '${nodeUtils.getName(node)}' from '${nodeUtils.getName(
    nodeParent
  )}' to '${nodeUtils.getName(nodeDestinationParent)}'.`;
};
